import { apiClient } from '@shared/services/apiClient';
import { getSessionId } from '@shared/hooks/useSessionId';
import { tokenService } from '@core/auth/services/tokenService';
import type {
  CartResponse,
  CartOperationResult,
  CartValidationResult,
  AddCartItemRequest,
  UpdateCartItemRequest,
  CartApiError,
  CartErrorCode,
} from '../types/cart.types';
import type { AxiosError, AxiosRequestConfig } from 'axios';

// =============================================================================
// Backend Response Types (snake_case)
// =============================================================================

interface ApiCartProductInfo {
  id: number;
  name: string;
  slug: string;
  thumbnail_url?: string;
  category_slug?: string | null;
}

interface ApiCartVariantInfo {
  id: number;
  sku: string;
  price: number;
  attributes: Record<string, string>;
  in_stock: boolean;
  quantity: number;
  product: ApiCartProductInfo;
}

interface ApiCartItemResponse {
  id: number;
  variant_id: number;
  quantity: number;
  unit_price: number;
  variant: ApiCartVariantInfo;
}

interface ApiCartResponse {
  id: number | null;
  user_id?: number | null;
  session_id?: string | null;
  status: 'active' | 'converted' | 'expired';
  items: ApiCartItemResponse[];
  subtotal: number;
  created_at: string | null;
  updated_at: string | null;
}

interface ApiCartOperationResult {
  cart: ApiCartResponse;
  adjusted?: boolean;
  message?: string | null;
}

interface ApiStockAdjustment {
  variant_id: number;
  requested_quantity: number;
  available_quantity: number;
  product_name: string;
}

interface ApiCartValidationResult {
  valid: boolean;
  adjustments: ApiStockAdjustment[];
}

// =============================================================================
// Transform Functions
// =============================================================================

function transformCartResponse(api: ApiCartResponse): CartResponse {
  return {
    id: api.id,
    userId: api.user_id,
    sessionId: api.session_id,
    status: api.status,
    items: api.items.map((item) => ({
      id: item.id,
      variantId: item.variant_id,
      quantity: item.quantity,
      unitPrice: item.unit_price,
      variant: {
        id: item.variant.id,
        sku: item.variant.sku,
        price: item.variant.price,
        attributes: item.variant.attributes,
        inStock: item.variant.in_stock,
        quantity: item.variant.quantity,
        product: {
          id: item.variant.product.id,
          name: item.variant.product.name,
          slug: item.variant.product.slug,
          thumbnailUrl: item.variant.product.thumbnail_url,
          categorySlug: item.variant.product.category_slug ?? null,
        },
      },
    })),
    subtotal: api.subtotal,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

function transformOperationResult(api: ApiCartOperationResult): CartOperationResult {
  return {
    cart: transformCartResponse(api.cart),
    adjusted: api.adjusted,
    message: api.message,
  };
}

function transformValidationResult(api: ApiCartValidationResult): CartValidationResult {
  return {
    valid: api.valid,
    adjustments: api.adjustments.map((adj) => ({
      variantId: adj.variant_id,
      requestedQuantity: adj.requested_quantity,
      availableQuantity: adj.available_quantity,
      productName: adj.product_name,
    })),
  };
}

// =============================================================================
// Request Config Helper
// =============================================================================

/**
 * Get request config with session ID header for guest users
 */
function getRequestConfig(sessionId?: string): AxiosRequestConfig {
  const config: AxiosRequestConfig = {};

  // Only add session ID for non-authenticated users
  const isAuthenticated = tokenService.hasAccessToken();

  if (!isAuthenticated) {
    const sid = sessionId || getSessionId();
    // Only add header if we have a valid session ID
    if (sid) {
      config.headers = {
        'X-Session-ID': sid,
      };
    }
  }

  return config;
}

// =============================================================================
// Error Handling
// =============================================================================

export class CartError extends Error {
  code: CartErrorCode;

  constructor(code: CartErrorCode, message: string) {
    super(message);
    this.name = 'CartError';
    this.code = code;
  }
}

function handleCartError(error: AxiosError<CartApiError>): never {
  if (error.response?.data?.error) {
    const { code, message } = error.response.data.error;
    throw new CartError(code, message);
  }

  // Handle 401 Unauthorized (no session and not authenticated)
  if (error.response?.status === 401) {
    throw new CartError('UNAUTHORIZED', 'Se requiere autenticación o sesión');
  }

  // Generic error
  throw new CartError('OUT_OF_STOCK', error.message || 'Error al procesar la operación del carrito');
}

// =============================================================================
// Cart Service
// =============================================================================

export const cartService = {
  /**
   * Get the current cart for the user or session
   * GET /cart
   */
  getCart: async (sessionId?: string): Promise<CartResponse> => {
    try {
      const config = getRequestConfig(sessionId);
      const response = await apiClient.get<ApiCartResponse>('/cart', config);
      return transformCartResponse(response.data);
    } catch (error) {
      return handleCartError(error as AxiosError<CartApiError>);
    }
  },

  /**
   * Add an item to the cart
   * POST /cart/items
   */
  addItem: async (
    request: AddCartItemRequest,
    sessionId?: string
  ): Promise<CartOperationResult> => {
    try {
      const config = getRequestConfig(sessionId);
      const payload = {
        variant_id: request.variantId,
        quantity: request.quantity,
      };
      const response = await apiClient.post<ApiCartOperationResult>(
        '/cart/items',
        payload,
        config
      );
      return transformOperationResult(response.data);
    } catch (error) {
      return handleCartError(error as AxiosError<CartApiError>);
    }
  },

  /**
   * Update quantity of an item in the cart
   * PATCH /cart/items/{variantId}
   */
  updateItem: async (
    variantId: number,
    request: UpdateCartItemRequest,
    sessionId?: string
  ): Promise<CartOperationResult> => {
    try {
      const config = getRequestConfig(sessionId);
      const response = await apiClient.patch<ApiCartOperationResult>(
        `/cart/items/${variantId}`,
        request,
        config
      );
      return transformOperationResult(response.data);
    } catch (error) {
      return handleCartError(error as AxiosError<CartApiError>);
    }
  },

  /**
   * Remove an item from the cart
   * DELETE /cart/items/{variantId}
   */
  removeItem: async (
    variantId: number,
    sessionId?: string
  ): Promise<CartOperationResult> => {
    try {
      const config = getRequestConfig(sessionId);
      const response = await apiClient.delete<ApiCartOperationResult>(
        `/cart/items/${variantId}`,
        config
      );
      return transformOperationResult(response.data);
    } catch (error) {
      return handleCartError(error as AxiosError<CartApiError>);
    }
  },

  /**
   * Clear all items from the cart
   * DELETE /cart
   */
  clearCart: async (sessionId?: string): Promise<CartOperationResult> => {
    try {
      const config = getRequestConfig(sessionId);
      const response = await apiClient.delete<ApiCartOperationResult>('/cart', config);
      return transformOperationResult(response.data);
    } catch (error) {
      const axiosError = error as AxiosError<CartApiError>;
      // Ignore 404 - cart may already be cleared, converted to order, or not exist
      if (axiosError.response?.status === 404) {
        // Silently return empty cart - this is expected after order creation
        return {
          cart: {
            id: 0,
            userId: null,
            sessionId: sessionId || null,
            status: 'active',
            items: [],
            subtotal: 0,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
          message: 'Cart already cleared',
        };
      }
      return handleCartError(axiosError);
    }
  },

  /**
   * Validate cart items against current stock levels
   * POST /cart/validate
   */
  validateCart: async (sessionId?: string): Promise<CartValidationResult> => {
    try {
      const config = getRequestConfig(sessionId);
      const response = await apiClient.post<ApiCartValidationResult>(
        '/cart/validate',
        {},
        config
      );
      return transformValidationResult(response.data);
    } catch (error) {
      return handleCartError(error as AxiosError<CartApiError>);
    }
  },
};

export default cartService;
